import Vue from 'vue';
import api from '../../api';
import moment from 'moment';
import getActionTemplate from '../../utils/action_templates';
import asyncForEach from '../../utils/asyncForEach';

let last_actions_request = null;

const state = {
  actions: [],
  action_types: {
    'To-do': {
      name: 'To-do',
      value: 'To-do',
      icon: '/icons/car.svg',
      id: 1,
    },
    Meeting: {
      name: 'Meeting',
      value: 'Meeting',
      icon: '/icons/car.svg',
      id: 2,
    },
    Event: { name: 'Event', value: 'Event', icon: '/icons/car.svg', id: 3 },
    Milestone: {
      name: 'Milestone',
      value: 'Milestone',
      icon: '/icons/car.svg',
      id: 4,
    },
    Contract: {
      name: 'Contract',
      value: 'Contract',
      icon: '/icons/car.svg',
      id: 5,
    },
    KPI: {
      name: 'KPI Review',
      value: 'KPI',
      icon: '/icons/car.svg',
      id: 6,
    },
    'Sign-off': {
      name: 'Sign-off',
      value: 'Sign-off',
      icon: '/icons/car.svg',
      id: 7,
    },
  },
  show_action_tray: false,
  current_action_id: null,
  current_action: null,
  new_action_group_id: -1,
  new_action_project_id: -1,
  getting_projects: false,
  refresh_actions_trigger: 0,
  action_tray_view_mode: 'tasks',
};

const getters = {
  actions(state) {
    return state.actions;
  },
  action_types(state) {
    return state.action_types;
  },
  current_action_id(state) {
    return state.current_action_id;
  },
  current_action(state) {
    return state.current_action;
  },
  new_action_group_id(state) {
    return state.new_action_group_id;
  },
  new_action_project_id() {
    return state.new_action_project_id;
  },
  show_action_tray(state) {
    return state.show_action_tray;
  },
  getting_projects(state) {
    return state.getting_projects;
  },
  refresh_actions_trigger(state) {
    return state.refresh_actions_trigger;
  },
  action_tray_view_mode(state) {
    return state.action_tray_view_mode;
  },
};

const mutations = {
  setActions(state, payload) {
    state.actions = payload;
  },
  setCurrentActionId(state, payload) {
    state.current_action_id = payload;
  },
  setCurrentAction(state, payload) {
    state.current_action = payload;
  },
  setNewActionGroupId(state, payload) {
    state.new_action_group_id = payload;
  },
  setNewActionProjectId(state, payload) {
    state.new_action_project_id = payload;
  },
  setActionTrayVisibility(state, payload) {
    state.show_action_tray = payload;
  },
  setGettingProjects(state, payload) {
    state.getting_projects = payload;
  },
  increment_refresh_actions_trigger() {
    state.refresh_actions_trigger = state.refresh_actions_trigger + 1;
  },
  setActionTrayViewMode(state, payload) {
    state.action_tray_view_mode = payload;
  },
};

const actions = {
  setActions(context, payload) {
    context.commit('setActions', payload);
  },
  setCurrentActionId(context, payload) {
    context.commit('setCurrentActionId', payload);
  },
  setCurrentAction(context, payload) {
    context.commit('setCurrentAction', payload);
  },
  setNewActionGroupId(context, payload) {
    context.commit('setNewActionGroupId', payload);
  },
  setNewActionProjectId(context, payload) {
    context.commit('setNewActionProjectId', payload);
  },
  setGettingProjects(context, payload) {
    context.commit('setGettingProjects', payload);
  },
  setActionTrayViewMode(context, payload) {
    context.commit('setActionTrayViewMode', payload);
  },
  sortProjectActions(context, groups) {
    groups.sort((a, b) => {
      // it is a new group
      if (!a.title && !a.startDate) {
        return -1;
      }

      if (!b.title && !b.startDate) {
        return 1;
      }

      // we know both dates
      if (a.startDate && b.startDate) {
        if (a.startDate.isBefore(b.startDate, 'd')) {
          return -1;
        }

        if (a.startDate.isSame(b.startDate, 'd')) {
          return sortById(a, b);
        }

        return 1;
      } else if (a.startDate && !b.startDate) {
        return -1;
      } else if (!a.startDate && b.startDate) {
        return 1;
      } else if (!a.startDate && !b.startDate) {
        return sortById(a, b);
      }

      return 1;
    });

    context.commit('setActions', groups);
  },
  getActions(context, params) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await api.getActions(params);

        // sort them into date groups, and add assignee details
        const users = context.getters.users;
        const today = moment();
        const tomorrow = moment().add(1, 'd');
        const actionDates = {};

        const add_date = (key, action) => {
          if (!actionDates[key]) {
            actionDates[key] = [];
          }
          actionDates[key].push(action);
        };

        const totalResults = res.data.actions.totalSize;
        const actionCount = res.data.actions.items.length;

        res.data.actions.items.forEach(action => {
          action.assignee = users[action.assigneeId] || null;

          if (!action.dueDate) {
            add_date('No due date', action);
          } else {
            const date = moment(action.dueDate);

            const is_overdue = date.isBefore(today, 'd');
            const is_today = date.isSame(today, 'd');
            const is_tomorrow = date.isSame(tomorrow, 'd');

            if (is_overdue) {
              add_date('Overdue', action);
            } else if (is_today) {
              add_date('Today', action);
            } else if (is_tomorrow) {
              add_date('Tomorrow', action);
            } else {
              add_date(action.dueDate, action);
            }
          }
        });

        // now sort them by date, then project, then actionGroupId
        Object.keys(actionDates).forEach(key => {
          actionDates[key] = actionDates[key].sort((a, b) => {
            const aDue = moment(a.dueDate);
            const bDue = moment(b.dueDate);
            if (!a.dueDate) {
              return 0;
            }
            if (aDue.isBefore(bDue, 'd')) {
              return -1;
            } else if (aDue.isAfter(bDue, 'd')) {
              return 1;
            } else if (a.projectId < b.projectId) {
              return -1;
            } else if (a.projectId > b.projectId) {
              return 1;
            } else if (a.actionGroupId < b.actionGroupId) {
              return -1;
            } else if (a.actionGroupId > b.actionGroupId) {
              return 1;
            } else if (a.title < b.title) {
              return -1;
            }
            return 1;
          });
        });

        resolve({ actionDates, totalResults, actionCount });
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  getProjectActions(context, { id, request_time }) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await api.getProjectActions(id);

        if (
          last_actions_request &&
          last_actions_request.isAfter(request_time)
        ) {
          // the user has requested the actions data again; so wait for that new request instead of processing these results
          resolve();
          return;
        }

        const users = context.getters.users;
        const groups = res.data.projects.items.map(group => {
          group.startDate = null;
          group.endDate = null;

          group.actions.forEach(action => {
            action.assignee = users[action.assigneeId] || null;

            if (action.dueDate) {
              const m = moment(action.dueDate);

              if (!group.startDate || m.isBefore(group.startDate)) {
                group.startDate = moment(action.dueDate);
              }

              if (!group.endDate || m.isAfter(group.endDate)) {
                group.endDate = moment(action.dueDate);
              }
            }
          });

          return group;
        });

        context.dispatch('sortProjectActions', groups);

        resolve(res);
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  createActionGroups(context, { actionGroups, project_id }) {
    return new Promise(async (resolve, reject) => {
      try {
        actionGroups.forEach(group => {
          delete group.id;
          delete group.startDate;
          delete group.endDate;
          delete group.actions;
        });
        const new_group = await api.createActionGroups(actionGroups);
        last_actions_request = moment();
        await context.dispatch('getProjectActions', {
          id: project_id,
          request_time: last_actions_request,
        });
        context.commit('increment_refresh_actions_trigger');
        resolve(new_group);
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  updateActionGroup(context, { actionGroup, project_id }) {
    return new Promise(async (resolve, reject) => {
      try {
        const id = actionGroup.id;
        const cloned_group = cloneActionGroup(actionGroup);
        await api.updateActionGroup(cloned_group, id);

        last_actions_request = moment();
        await context.dispatch('getProjectActions', {
          id: project_id,
          request_time: last_actions_request,
        });
        context.commit('increment_refresh_actions_trigger');

        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  deleteActionGroups(context, { actionGroups, project_id }) {
    return new Promise(async (resolve, reject) => {
      try {
        context.getters.actions.forEach((group, index) => {
          if (actionGroups.includes(group.id)) {
            Vue.delete(context.getters.actions, index);
          }
        });

        await api.deleteActionGroups(actionGroups);

        last_actions_request = moment();
        await context.dispatch('getProjectActions', {
          id: project_id,
          request_time: last_actions_request,
        });

        context.commit('increment_refresh_actions_trigger');
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  createActions(
    context,
    { action_group_id, actions, project_id, refresh_after }
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        context.getters.actions.forEach(group => {
          if (group.id === action_group_id) {
            actions.forEach(action => {
              action.id = Date.now();
              group.actions.push(action);
            });
          }
        });

        const cloned_actions = cloneActions(actions, true);
        const users = context.getters.users;
        const saved_projects = await api.createActions(
          action_group_id,
          cloned_actions
        );
        saved_projects.data.createdActions.forEach(action => {
          action.assignee = users[action.assigneeId] || null;
          action.linkedDocs = action.linkedDocs || [];
        });

        last_actions_request = moment();
        if (refresh_after) {
          await context.dispatch('getProjectActions', {
            id: project_id,
            request_time: last_actions_request,
          });
        }

        context.commit('increment_refresh_actions_trigger');
        resolve(saved_projects.data.createdActions);
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  updateAction(context, { action, project_id, refresh_after }) {
    return new Promise(async (resolve, reject) => {
      try {
        const id = action.id;
        const cloned_action = processAction(action, false);
        delete cloned_action.project;
        delete cloned_action.actionGroup;
        await api.updateAction(cloned_action, id);

        last_actions_request = moment();

        if (refresh_after) {
          if (!project_id) {
            project_id = action.projectId;
          }
          await context.dispatch('getProjectActions', {
            id: project_id,
            request_time: last_actions_request,
          });
        }

        context.commit('increment_refresh_actions_trigger');
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  deleteActions(context, { actions, project_id, refresh_after }) {
    return new Promise(async (resolve, reject) => {
      try {
        context.getters.actions.forEach((group, index) => {
          group.actions.forEach((action, ind) => {
            if (actions.includes(action.id)) {
              Vue.delete(context.getters.actions[index].actions, ind);
            }
          });
        });

        await api.deleteActions(actions);

        last_actions_request = moment();
        if (refresh_after) {
          await context.dispatch('getProjectActions', {
            id: project_id,
            request_time: last_actions_request,
          });
        }

        context.commit('increment_refresh_actions_trigger');
        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  setActionTrayVisibility(context, payload) {
    context.commit('setActionTrayVisibility', payload);
  },
  saveTemplatedActions(context, { type, project_id }) {
    return new Promise(async (resolve, reject) => {
      try {
        const app_user = context.getters.app_user;
        const userId = app_user.role === 'SuperAdmin' ? null : app_user.id;
        const groups = getActionTemplate(type, project_id, userId);

        const api_groups = groups.map(group => {
          return {
            title: group.title,
            projectId: group.projectId,
          };
        });

        const res = await api.createActionGroups(api_groups);

        res.data.createdActionGroups.forEach(group => {
          groups.forEach(g => {
            if (g.title === group.title) {
              g.actionGroupId = group.id;
            }
          });
        });

        await asyncForEach(groups, async group => {
          await api.createActions(group.actionGroupId, group.actions);
        });

        resolve();
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  getActionTemplateGuidance(context, actionTemplateId) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await api.getActionTemplateGuidance(actionTemplateId);
        if (res && res.data && res.data.guidance) {
          resolve(res.data.guidance);
        } else {
          reject(new Error('Error getting guidance'))
        }
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  },
  getActionTemplatesGuidance(context, actionTemplateIds) {
    return new Promise(async (resolve, reject) => {
      try {
        const res = await api.getActionTemplatesGuidance([{key: 'ids', value: actionTemplateIds}]);
        if (res && res.data && res.data.guidance) {
          resolve(res.data.guidance);
        } else {
          reject(new Error('Error getting guidance'))
        }
      } catch (e) {
        console.error(e);
        reject(e);
      }
    });
  }
};

const cloneActionGroup = group => {
  const cloned_group = JSON.parse(JSON.stringify(group));

  delete cloned_group.startDate;
  delete cloned_group.endDate;
  delete cloned_group.project;
  delete cloned_group.actions;
  delete cloned_group.createdAt;
  delete cloned_group.updatedAt;
  delete cloned_group.id;

  return cloned_group;
};

const cloneActions = (actions, isNew) => {
  const cloned_actions = JSON.parse(JSON.stringify(actions));
  return cloned_actions.map(action => processAction(action, isNew));
};

const processAction = (action, isNew) => {
  const processed_action = JSON.parse(JSON.stringify(action));

  processed_action.assigneeId = processed_action.assignee
    ? processed_action.assignee.id
    : null;

  delete processed_action.id;
  delete processed_action.createdAt;
  delete processed_action.updatedAt;
  delete processed_action.ActionGroupToActions;
  delete processed_action.assignee;

  if (isNew) {
    delete processed_action.actionGroupId;
  }

  processed_action.linkedDocs =
    processed_action.linkedDocs.map(doc => doc.id) || [];

  processed_action.linkedFiles =
    processed_action.linkedFiles ? processed_action.linkedFiles.map(file => file.id) : [];

  return processed_action;
};

const sortById = (a, b) => {
  if (a.id < 0) {
    return 1;
  }

  if (a.id < b.id) {
    return -1;
  }
  return 1;
};

export default {
  state,
  getters,
  mutations,
  actions,
};
